import React, {useCallback, useState} from 'react';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import Group from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {useSsoLoginLink} from '../../hooks/use-sso-login-link';
import {useSsoSessionStorage} from '../../hooks/use-sso-session-storage';
import {
    INITIAL_DOMAIN_DATA,
    MAX_DOMAINS,
    areDomainsValid,
    getSelectedDomains,
} from '../../utils/domain-validation';
import {DomainManager} from '../domain-manager/domain-manager';
import {StepFooter} from '../step-footer/step-footer';

import {
    confirmationRow,
    domainChip,
    domainChipList,
    domainsCard,
    domainsCardFooter,
    domainsCardHeader,
    infoBanner,
    infoBannerSubtitle,
    infoBannerTitle,
    instructionsCard,
    instructionsList,
    testEndUserContainer,
} from './test-end-user.module.styl';

import type {VerifyDomainData} from '../../utils/domain-validation';


type TestEndUserProps = {
    onContinue: () => void,
    onBack: () => void,
    saving?: boolean,
};

type TestEndUserData = {
    confirmed: boolean,
};

const INITIAL_DATA: TestEndUserData = {
    confirmed: false,
};

function getCheckedValue(payload: unknown): boolean {
    if (typeof payload === 'boolean') {
        return payload;
    }
    if (payload != null && typeof payload === 'object') {
        const eventTarget = (payload as {event?: {target?: {checked?: boolean}}}).event?.target;
        if (typeof eventTarget?.checked === 'boolean') {
            return eventTarget.checked;
        }
        const target = (payload as {target?: {checked?: boolean}}).target;
        if (typeof target?.checked === 'boolean') {
            return target.checked;
        }
        const checked = (payload as {checked?: boolean}).checked;
        if (typeof checked === 'boolean') {
            return checked;
        }
    }
    return false;
}

export const TestEndUser: React.FC<TestEndUserProps> = ({onContinue, onBack, saving}) => {
    const [data, setData] = useSsoSessionStorage<TestEndUserData>('testEndUser', INITIAL_DATA);
    const [domainData, setDomainData] = useSsoSessionStorage<VerifyDomainData>('verifyDomain', INITIAL_DOMAIN_DATA);
    const [manualExpanded, setManualExpanded] = useState(false);
    const {url: ssoLoginUrl} = useSsoLoginLink();

    const allDomains = getSelectedDomains(domainData.domains).map(d => d.value).filter(Boolean);
    const allDomainsText = allDomains.join(', ');
    const domainsValid = areDomainsValid(domainData.domains);
    const domainsExpanded = manualExpanded || !domainsValid || domainData.domains.length > MAX_DOMAINS;

    const handleCheckboxChange = useCallback((payload: unknown) => {
        setData({confirmed: getCheckedValue(payload)});
    }, [setData]);

    return (
        <Group direction="v" gap="l" className={testEndUserContainer}>
            <Group direction="v" gap="xs">
                <Typography variant="heading-level-1">
                    Test with an End User
                </Typography>
                <Typography variant="body-level-1">
                    Before enforcing SSO for your organization, verify that a real user can
                    successfully sign in to PitchBook using the SSO configuration.
                </Typography>
            </Group>

            <div className={infoBanner}>
                <span className={infoBannerTitle}>
                    During testing, other users from your organization may be directed to sign in
                    with your Identity Provider.
                </span>
                <br/>
                <span className={infoBannerSubtitle}>
                    Users who are not part of the test should select &quot;Sign in using another method&quot;
                    to continue signing in with username and password.
                </span>
                <br/>
                <span className={infoBannerSubtitle}>
                    You must successfully test before enabling Company Login.
                </span>
            </div>

            <div className={domainsCard}>
                <div className={domainsCardHeader}>
                    <Typography variant="heading-level-3">
                        Email Domains
                    </Typography>
                    {domainsValid && !manualExpanded ? (
                        <Button variant="Type.SECONDARY" onClick={() => setManualExpanded(true)}>
                            Manage domains
                        </Button>
                    ) : null}
                </div>
                {domainsExpanded ? (
                    <>
                        <DomainManager domainData={domainData} onDomainDataChange={setDomainData}/>
                        {manualExpanded ? (
                            <div className={domainsCardFooter}>
                                <Button
                                    variant="Type.SECONDARY"
                                    onClick={() => setManualExpanded(false)}
                                    disabled={!domainsValid}>
                                    Done
                                </Button>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className={domainChipList}>
                        {allDomains.map(domain => (
                            <span key={domain} className={domainChip}>{domain}</span>
                        ))}
                    </div>
                )}
            </div>

            <div className={instructionsCard}>
                <Typography variant="heading-level-3">
                    What to do
                </Typography>

                {allDomains.length > 0 ? (
                    <Typography variant="body-level-1">
                        The user's email must end with a domain from the list:{' '}<strong>{allDomainsText}</strong>.
                    </Typography>
                ) : null}

                <ol className={instructionsList}>
                    <li>
                        Before you start testing, review the email domains listed above. If you need to make
                        changes, use <strong>Manage domains</strong>.
                    </li>
                    <li>
                        Make sure your user has an account in your Identity Provider and is assigned to the
                        PitchBook SAML application. Without this assignment, you will not be able to log in.
                    </li>
                    <li>
                        Open{' '}
                        <a href={ssoLoginUrl} target="_blank" rel="noopener noreferrer">
                            https://my.pitchbook.com
                        </a>.
                    </li>
                    <li>
                        Enter your corporate email address and follow your Identity Provider&apos;s login prompts.
                    </li>
                    <li>
                        If required by your organization, complete any multi-factor authentication steps.
                    </li>
                    <li>
                        After signing in, verify that you land on the PitchBook home page. If you see an error, capture
                        the error message and review your SSO configuration, including attribute mappings, certificates,
                        and URLs, using the guidance from the previous steps.
                    </li>
                    <li>
                        Return to this configuration page and confirm the test was successful by checking the box below.
                    </li>
                </ol>
            </div>

            <Group alignItems="center" gap="s" className={confirmationRow}>
                <CheckBox
                    checked={data.confirmed}
                    onCheckBoxChange={handleCheckboxChange}
                    usePropsOnly={true}
                />
                <Typography variant="body-level-1">
                    I confirm that a user from my organization has successfully signed in to PitchBook using SSO.
                </Typography>
            </Group>

            <StepFooter
                onContinue={onContinue}
                onBack={onBack}
                continueDisabled={!data.confirmed || !domainsValid}
                busy={saving}
            />
        </Group>
    );
};
